/**
 * Tracks refetch callbacks for API instances.
 * When a mutation (non-GET) succeeds on ANY API, ALL registered
 * useFetch listeners are refetched — not just the ones watching
 * the mutated API. This ensures cross-API mutations (e.g. a
 * QuotesApi mutation that affects data fetched via ShipmentsApi)
 * always trigger a refresh.
 */

type RefetchCallback = () => void;

// Global set of all active refetch callbacks across all APIs.
const allCallbacks = new Set<RefetchCallback>();

export function registerRefetch(_api: object, callback: RefetchCallback): void {
  allCallbacks.add(callback);
}

export function unregisterRefetch(
  _api: object,
  callback: RefetchCallback,
): void {
  allCallbacks.delete(callback);
}

/**
 * Depth counter to suppress notifyRefetch during useFetch reads.
 *
 * When useFetch calls its fetch function, the API method goes through the
 * Proxy which detects non-GET methods as mutations and calls notifyRefetch.
 * But useFetch is a READ — it shouldn't trigger refetch of itself or others.
 * Incrementing this counter during the fetch suppresses the notification.
 *
 * Uses a counter instead of a boolean so concurrent fetches don't break
 * each other's suppression (the fast fetch finishing won't reset the flag
 * while the slow fetch is still in flight).
 *
 * Example: generateProfitLossReport is a POST that returns data (a read
 * disguised as POST). Without suppression: useFetch calls it → Proxy sees
 * POST → notifyRefetch → useFetch refetches → infinite loop.
 */
let suppressDepth = 0;

export function withSuppressedRefetch<T>(fn: () => Promise<T>): Promise<T> {
  suppressDepth++;
  return fn().finally(() => {
    suppressDepth--;
  });
}

export function notifyRefetch(_api: object): void {
  if (suppressDepth > 0) return;
  for (const callback of allCallbacks) {
    callback();
  }
}
